import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { Translation } from '../../../core/i18n/translation.service';
import { LandingAssistantService } from '../../../core/services/landing-assistant.service';
import { LandingAssistantStore } from '../../../core/services/landing-assistant.store';

@Component({
  selector: 'app-landing-assistant-widget',
  standalone: true,
  imports: [FormsModule, RouterLink, TranslatePipe],
  templateUrl: './landing-assistant-widget.html',
  styleUrl: './landing-assistant-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingAssistantWidget {
  protected readonly store = inject(LandingAssistantStore);
  private readonly assistant = inject(LandingAssistantService);
  private readonly translation = inject(Translation);

  protected readonly draft = signal('');
  protected readonly leadFormOpen = signal(false);
  protected readonly leadSubmitting = signal(false);
  protected readonly leadSubmitted = signal(false);
  protected readonly leadError = signal<string | null>(null);

  protected readonly leadName = signal('');
  protected readonly leadEmail = signal('');
  protected readonly leadPhone = signal('');
  protected readonly leadCompany = signal('');
  protected readonly leadHoneypot = signal('');

  protected readonly showLeadPrompt = computed(
    () => this.store.lastAction() === 'offerLeadForm' && !this.leadFormOpen() && !this.leadSubmitted(),
  );

  protected readonly showRegistrationPrompt = computed(
    () => this.store.lastAction() === 'suggestRegistration' && !this.leadFormOpen(),
  );

  protected toggle(): void {
    this.store.toggleOpen();
  }

  protected send(): void {
    const content = this.draft();
    // Only clear the draft when the store actually accepted the message, otherwise a
    // send attempted while another is in flight would silently discard the typed text.
    if (this.store.sendMessage(content, this.translation.getCurrentLang())) {
      this.draft.set('');
    }
  }

  protected openLeadForm(): void {
    this.leadFormOpen.set(true);
    this.leadSubmitted.set(false);
    this.leadError.set(null);
  }

  protected submitLead(): void {
    if (this.leadSubmitting()) {
      return;
    }
    if (!this.leadFormValid()) {
      this.leadError.set('LANDING.ASSISTANT.LEAD.INVALID');
      return;
    }

    this.leadSubmitting.set(true);
    this.leadError.set(null);

    const transcript = this.store
      .messages()
      .slice(-6)
      .map((m) => `${m.role}: ${m.content}`)
      .join('\n');

    this.assistant
      .submitLead({
        name: this.leadName().trim(),
        email: this.leadEmail().trim(),
        phone: this.leadPhone().trim(),
        company: this.leadCompany().trim(),
        locale: this.translation.getCurrentLang(),
        conversationContext: transcript || undefined,
        honeypot: this.leadHoneypot() || undefined,
      })
      .pipe(finalize(() => this.leadSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.leadSubmitted.set(true);
          this.leadFormOpen.set(false);
          // The thank-you lives in the transcript (which survives navigation via the
          // root-scoped store) instead of replacing the composer, and the AI-driven
          // action is cleared so the "leave your details" prompt does not reappear
          // after PublicShellLayout is re-instantiated on the next route change.
          this.store.clearAction();
          this.store.addAssistantMessage(this.translation.instant('LANDING.ASSISTANT.LEAD.THANKS'));
        },
        error: () => {
          this.leadError.set('LANDING.ASSISTANT.LEAD.ERROR');
        },
      });
  }

  /** Name, work email, phone and company are all required before a lead is sent. */
  private leadFormValid(): boolean {
    const email = this.leadEmail().trim();
    return (
      this.leadName().trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
      this.leadPhone().trim().length > 0 &&
      this.leadCompany().trim().length > 0
    );
  }
}
